/**
 * Utility functions for calculating recipe allergens from ingredients
 */

/**
 * Normalize allergen name to prevent case-sensitive and semantic duplicates
 * @param {string} allergen - The allergen name
 * @returns {string} - Normalized allergen name with consistent formatting and semantic deduplication
 */
const normalizeAllergen = allergen => {
  if (!allergen || typeof allergen !== 'string') return ''

  // First normalize case
  let normalized =
    allergen.charAt(0).toUpperCase() + allergen.slice(1).toLowerCase()

  // Handle semantic duplicates - map variations to a standard form
  const allergenMappings = {
    'Sulphur dioxide/sulphites': 'Sulphites',
    'Sulphur dioxide/sulphite': 'Sulphites',
    'Sulphur dioxide': 'Sulphites',
    Sulphites: 'Sulphites',
    Sulphite: 'Sulphites',
    'Sesame seeds': 'Sesame seeds',
    Sesame: 'Sesame seeds',
    'Cereals containing gluten': 'Gluten',
    Gluten: 'Gluten',
    Nuts: 'Nuts',
    'Tree nuts': 'Nuts',
    Peanuts: 'Peanuts',
    'Ground nuts': 'Peanuts',
    Fish: 'Fish',
    Celery: 'Celery',
    Milk: 'Milk',
    Dairy: 'Milk',
    Eggs: 'Eggs',
    Egg: 'Eggs',
    Crustaceans: 'Crustaceans',
    Shellfish: 'Crustaceans',
    Molluscs: 'Molluscs',
    Mollusc: 'Molluscs',
    Mustard: 'Mustard',
    Lupin: 'Lupin',
    Soya: 'Soya',
    Soy: 'Soya',
    Soybeans: 'Soya',
  }

  // Check if we have a mapping for this allergen
  if (allergenMappings[normalized]) {
    return allergenMappings[normalized]
  }

  // If no mapping found, return the case-normalized version
  return normalized
}

function resolveStackedRecipe(recipeIngredient, allRecipes) {
  const childId = recipeIngredient.recipe?._id || recipeIngredient.recipeId
  if (!childId) return null
  const fromList = Array.isArray(allRecipes)
    ? allRecipes.find(
        r => r._id === childId || String(r._id) === String(childId),
      )
    : null
  return fromList || recipeIngredient.recipe || null
}

export const calculateRecipeAllergens = (
  recipe,
  venueProducts,
  allRecipes = null,
  visited = new Set(),
) => {
  if (!recipe || !venueProducts || !Array.isArray(venueProducts)) {
    return { allergens: [], allergenTraces: [] }
  }

  const recipeId = recipe._id ? String(recipe._id) : null
  if (recipeId && visited.has(recipeId)) {
    return { allergens: [], allergenTraces: [] }
  }
  if (recipeId) visited.add(recipeId)

  const allAllergens = new Set()
  const allAllergenTraces = new Set()

  // Process regular ingredients
  if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
    recipe.ingredients.forEach(ingredient => {
      const ingredientAllergens = getIngredientAllergens(
        ingredient,
        venueProducts,
      )
      ingredientAllergens.allergens.forEach(allergen =>
        allAllergens.add(normalizeAllergen(allergen)),
      )
      ingredientAllergens.allergenTraces.forEach(trace =>
        allAllergenTraces.add(normalizeAllergen(trace)),
      )
    })
  }

  // Process custom ingredients - they have allergens stored directly on them
  // Check both customIngredients (from API) and customProducts (from form state)
  const customIngredients =
    recipe.customIngredients || recipe.customProducts || []

  if (customIngredients && Array.isArray(customIngredients)) {
    customIngredients.forEach(ingredient => {
      // Custom ingredients have allergens directly on them (not in a product field)
      if (ingredient.allergens && Array.isArray(ingredient.allergens)) {
        ingredient.allergens.forEach(allergen =>
          allAllergens.add(normalizeAllergen(allergen)),
        )
      }
      if (
        ingredient.allergenTraces &&
        Array.isArray(ingredient.allergenTraces)
      ) {
        ingredient.allergenTraces.forEach(trace =>
          allAllergenTraces.add(normalizeAllergen(trace)),
        )
      }

      // Also check if allergens are in a populated product field (fallback)
      if (
        ingredient.product &&
        ingredient.product.allergens &&
        Array.isArray(ingredient.product.allergens)
      ) {
        ingredient.product.allergens.forEach(allergen =>
          allAllergens.add(normalizeAllergen(allergen)),
        )
      }
      if (
        ingredient.product &&
        ingredient.product.allergenTraces &&
        Array.isArray(ingredient.product.allergenTraces)
      ) {
        ingredient.product.allergenTraces.forEach(trace =>
          allAllergenTraces.add(normalizeAllergen(trace)),
        )
      }

      // Also check venue products for custom ingredients (fallback)
      const ingredientAllergens = getIngredientAllergens(
        ingredient,
        venueProducts,
      )
      ingredientAllergens.allergens.forEach(allergen =>
        allAllergens.add(normalizeAllergen(allergen)),
      )
      ingredientAllergens.allergenTraces.forEach(trace =>
        allAllergenTraces.add(normalizeAllergen(trace)),
      )
    })
  }

  // Process recipe ingredients (stacked recipes) – recurse so allergens pull through from
  // OMS ingredients, custom ingredients, and further stacked recipes
  if (recipe.recipeIngredients && Array.isArray(recipe.recipeIngredients)) {
    recipe.recipeIngredients.forEach(recipeIngredient => {
      const child = resolveStackedRecipe(recipeIngredient, allRecipes)
      if (child) {
        const childResult = calculateRecipeAllergens(
          child,
          venueProducts,
          allRecipes,
          visited,
        )
        childResult.allergens.forEach(a =>
          allAllergens.add(normalizeAllergen(a)),
        )
        childResult.allergenTraces.forEach(t =>
          allAllergenTraces.add(normalizeAllergen(t)),
        )
      }
      // Fallback: use stored allergens on populated recipe if no recursion was done
      if (!child && recipeIngredient.recipe) {
        const r = recipeIngredient.recipe
        if (r.allergens && Array.isArray(r.allergens)) {
          r.allergens.forEach(allergen =>
            allAllergens.add(normalizeAllergen(allergen)),
          )
        }
        if (r.allergenTraces && Array.isArray(r.allergenTraces)) {
          r.allergenTraces.forEach(trace =>
            allAllergenTraces.add(normalizeAllergen(trace)),
          )
        }
      }
    })
  }

  // Convert sets to arrays and sort for consistency
  return {
    allergens: Array.from(allAllergens).sort(),
    allergenTraces: Array.from(allAllergenTraces).sort(),
  }
}

/**
 * Get allergen data for a specific ingredient
 * @param {Object} ingredient - The ingredient object
 * @param {Array} venueProducts - Array of venue products with allergen data
 * @returns {Object} - Object containing allergens and allergenTraces arrays
 */
const getIngredientAllergens = (ingredient, venueProducts) => {
  if (!ingredient) {
    return { allergens: [], allergenTraces: [] }
  }

  let allergens = []
  let allergenTraces = []
  let venueProduct = null // Move venueProduct to function scope

  // PRIORITY 1: Check venue products first for the most up-to-date allergen data
  // This ensures we use fresh data after allergen updates, not cached ingredient data
  if (venueProducts && Array.isArray(venueProducts)) {
    // First try by product._id
    if (ingredient.product && ingredient.product._id) {
      venueProduct = venueProducts.find(
        vp => vp.product && vp.product._id === ingredient.product._id,
      )
    }

    // If not found, try by productId
    if (!venueProduct && ingredient.productId) {
      venueProduct = venueProducts.find(
        vp => vp.product && vp.product._id === ingredient.productId,
      )
    }

    // If not found, try by venueProductId
    if (!venueProduct && ingredient.venueProductId) {
      venueProduct = venueProducts.find(
        vp => vp._id === ingredient.venueProductId,
      )
    }

    // If still not found, try by _id (for custom products)
    if (!venueProduct && ingredient._id) {
      venueProduct = venueProducts.find(vp => vp._id === ingredient._id)
    }

    // If still not found, try fuzzy matching by title
    if (!venueProduct && ingredient.title) {
      venueProduct = venueProducts.find(
        vp =>
          vp.title &&
          vp.title.toLowerCase().includes(ingredient.title.toLowerCase()),
      )
    }

    // CRITICAL FIX: If we found a venue product, use its allergen data
    // We should use venue product data if:
    // 1. The venueProduct exists, AND
    // 2. Either allergensEditedByUser is true (user has explicitly set allergens)
    //    OR the allergen arrays exist (even if empty - empty means "no allergens")
    //
    // This ensures that when a user sets all allergens to "No" (empty arrays),
    // we don't fall back to the base product allergens
    if (venueProduct) {
      // If allergens have been edited by user, always use venue product data (even if empty)
      // If not edited, only use if arrays have data
      const useVenueProductAllergens =
        venueProduct.allergensEditedByUser ||
        (venueProduct.allergens && venueProduct.allergens.length > 0)

      const useVenueProductTraces =
        venueProduct.allergensEditedByUser ||
        (venueProduct.allergenTraces && venueProduct.allergenTraces.length > 0)

      if (useVenueProductAllergens) {
        allergens = venueProduct.allergens || []
      }
      if (useVenueProductTraces) {
        allergenTraces = venueProduct.allergenTraces || []
      }
    }
  }

  // CRITICAL FIX: Only fallback to base product allergens if:
  // 1. No venue product was found, AND
  // 2. User has NOT explicitly edited allergens (allergensEditedByUser = false)
  //
  // If user has edited allergens (even to empty arrays), NEVER use base product data
  // This prevents historic allergens from reappearing when user sets all to "No"
  const shouldUseFallback =
    allergens.length === 0 &&
    allergenTraces.length === 0 &&
    ingredient.product &&
    (ingredient.product.allergens || ingredient.product.allergenTraces) &&
    (!venueProduct || !venueProduct.allergensEditedByUser) // Only if not edited by user

  if (shouldUseFallback) {
    allergens = ingredient.product.allergens || []
    allergenTraces = ingredient.product.allergenTraces || []
  }

  return {
    allergens,
    allergenTraces,
  }
}

export const calculateAllRecipesAllergens = (
  recipes,
  venueProducts,
  allRecipes = null,
) => {
  if (!recipes || !Array.isArray(recipes)) {
    return []
  }

  const lookupRecipes =
    Array.isArray(allRecipes) && allRecipes.length > 0 ? allRecipes : recipes

  return recipes.map(recipe => {
    const calculatedAllergens = calculateRecipeAllergens(
      recipe,
      venueProducts,
      lookupRecipes,
      new Set(),
    )
    return {
      ...recipe,
      allergens: calculatedAllergens.allergens,
      allergenTraces: calculatedAllergens.allergenTraces,
    }
  })
}
